import {
  AppstoreOutlined,
  BarChartOutlined,
  DeleteOutlined,
  NumberOutlined,
  TableOutlined,
  UnorderedListOutlined
} from "@ant-design/icons"
import { Button, Empty, Flex, Radio, Select, Tabs, Typography } from "antd"
import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import { neutral } from "../../../../components/widgets/tokens.ts"
import type { ChartVisualisation } from "../../../../components/widgets/types.ts"
import { filterByLabelOrValue } from "../../../DashboardPage/components/Portfolio/FilterBar.tsx"
import { createEmptyReportWidgetFilters } from "../../helpers/reportWidgetFilters.ts"
import { useReportBuildActions } from "../../hooks/useReportBuildActions.ts"
import { useReportFilterOptions } from "../../hooks/useReportFilterOptions.ts"
import {
  REPORT_COMPARE_OVERRIDES,
  type ReportCompareOverride,
  type ReportFilterDimension,
  WIDGET_SIZE_PRESETS
} from "../../types/canvas.types.ts"
import { TextInput } from "../../../../components/Input/TextInput.tsx"

const { Text } = Typography

const COMPARE_OPTION_LABEL_KEYS: Record<ReportCompareOverride, string> = {
  inherit: "reports.widgetConfig.compareOptions.inherit",
  none: "reports.widgetConfig.compareOptions.none",
  previous_period: "reports.widgetConfig.compareOptions.previousPeriod",
  same_period_last_year: "reports.widgetConfig.compareOptions.samePeriodLastYear"
}

const FILTER_LABEL_KEYS: Record<ReportFilterDimension, string> = {
  project: "reports.widgetConfig.filters.project",
  pm: "reports.widgetConfig.filters.pm",
  client: "reports.widgetConfig.filters.client",
  project_type: "reports.widgetConfig.filters.projectType",
  project_status: "reports.widgetConfig.filters.projectStatus",
  status: "reports.widgetConfig.filters.status",
  state: "reports.widgetConfig.filters.state"
}

const FILTER_DIMENSION_ORDER: ReportFilterDimension[] = [
  "project",
  "pm",
  "client",
  "project_type",
  "project_status",
  "status",
  "state"
]

const KIND_ICON: Record<string, React.ReactNode> = {
  kpi: <NumberOutlined />,
  chart: <BarChartOutlined />,
  table: <TableOutlined />,
  list: <UnorderedListOutlined />
}

const KIND_KEYS = new Set([
  "kpi",
  "chart",
  "table",
  "list",
  "progress",
  "feed",
  "alert"
])

export const ReportWidgetSettingsPanel = () => {
  const { t } = useTranslation()
  const { options, loading } = useReportFilterOptions()
  const {
    selectedWidget,
    selectedSpec,
    visualisationOptions,
    removeWidget,
    changeTitle,
    changeSize,
    changeFilters,
    changeCompare,
    changeVisualisation
  } = useReportBuildActions()

  const [titleDraft, setTitleDraft] = useState("")

  useEffect(() => {
    setTitleDraft(selectedWidget?.title ?? "")
  }, [selectedWidget?.id, selectedWidget?.title])

  const panelStyle: React.CSSProperties = {
    width: 320,
    flex: "none",
    borderLeft: `1px solid ${neutral.border}`,
    paddingLeft: 16,
    overflow: "auto"
  }

  if (!selectedWidget) {
    return (
      <Flex vertical gap="middle" style={panelStyle}>
        <Text strong>{t("reports.settings.title")}</Text>
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={t("reports.settings.noSelection")}
        />
      </Flex>
    )
  }

  const widget = selectedWidget
  const filters = widget.filters ?? createEmptyReportWidgetFilters()
  const kind = selectedSpec?.type ?? "widget"
  const kindLabel = t(
    `reports.settings.kinds.${KIND_KEYS.has(kind) ? kind : "widget"}`
  )

  const handleTitleBlur = () => {
    if (titleDraft.trim() === (widget.title ?? "")) return
    changeTitle(widget.id, titleDraft)
  }

  const handleFilterChange =
    (dimension: ReportFilterDimension) => (value: string[]) =>
      changeFilters(widget.id, { ...filters, [dimension]: value })

  const dataTab = (
    <Flex vertical gap="large">
      <Flex vertical gap="small">
        <Text strong>{t("reports.widgetConfig.fields.compare")}</Text>
        <Select<ReportCompareOverride>
          value={widget.compare ?? "inherit"}
          onChange={(value) => changeCompare(widget.id, value)}
          options={REPORT_COMPARE_OVERRIDES.map((option) => ({
            label: t(COMPARE_OPTION_LABEL_KEYS[option]),
            value: option
          }))}
        />
      </Flex>

      <Flex vertical gap="small">
        <Flex justify="space-between" align="center">
          <Text strong>{t("reports.widgetConfig.filters.title")}</Text>
          <Button
            size="small"
            onClick={() => changeFilters(widget.id, undefined)}
          >
            {t("reports.widgetConfig.filters.clear")}
          </Button>
        </Flex>
        {FILTER_DIMENSION_ORDER.map((dimension) => (
          <Select<string[]>
            key={dimension}
            mode="multiple"
            allowClear
            showSearch
            loading={loading}
            maxTagCount="responsive"
            filterOption={filterByLabelOrValue}
            placeholder={t(FILTER_LABEL_KEYS[dimension])}
            value={filters[dimension] ?? []}
            options={options[dimension]}
            onChange={handleFilterChange(dimension)}
          />
        ))}
      </Flex>
    </Flex>
  )

  const styleTab = (
    <Flex vertical gap="large">
      {visualisationOptions && (
        <Flex vertical gap="small">
          <Text strong>{t("reports.widgetConfig.fields.visualisation")}</Text>
          <Select<ChartVisualisation>
            allowClear
            value={widget.visualisation}
            placeholder={t("reports.widgetConfig.visualisationOptions.default")}
            onChange={(value) =>
              changeVisualisation(widget.id, value ?? undefined)
            }
            options={visualisationOptions.map((option) => ({
              label: t(`reports.widgetConfig.visualisationOptions.${option}`),
              value: option
            }))}
          />
        </Flex>
      )}

      <Flex vertical gap="small">
        <Text strong>{t("reports.widgetConfig.fields.size")}</Text>
        <Radio.Group
          optionType="button"
          buttonStyle="solid"
          value={widget.size}
          onChange={(e) => changeSize(widget.id, e.target.value)}
          options={WIDGET_SIZE_PRESETS.map((preset) => ({
            label: t(`reports.widgetConfig.sizeOptions.${preset}`),
            value: preset
          }))}
        />
      </Flex>
    </Flex>
  )

  const advancedTab = (
    <Flex vertical gap="small">
      <Text strong>{t("reports.widgetConfig.fields.title")}</Text>
      <TextInput
        value={titleDraft}
        placeholder={selectedSpec?.title ?? ""}
        onChange={(e) => setTitleDraft(e.target.value)}
        onBlur={handleTitleBlur}
      />
    </Flex>
  )

  return (
    <Flex vertical gap="middle" style={panelStyle}>
      <Text strong>{t("reports.settings.title")}</Text>

      <Flex vertical gap={4}>
        <Text type="secondary" style={{ fontSize: 12 }}>
          {t("reports.settings.selectedWidget")}
        </Text>
        <Flex align="center" gap="small">
          <span style={{ color: neutral.muted, fontSize: 18 }}>
            {KIND_ICON[kind] ?? <AppstoreOutlined />}
          </span>
          <Flex vertical className="min-w-0">
            <Text strong ellipsis>
              {widget.title || selectedSpec?.title}
            </Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {kindLabel}
            </Text>
          </Flex>
        </Flex>
      </Flex>

      <Tabs
        items={[
          { key: "data", label: t("reports.settings.tabs.data"), children: dataTab },
          {
            key: "style",
            label: t("reports.settings.tabs.style"),
            children: styleTab
          },
          {
            key: "advanced",
            label: t("reports.settings.tabs.advanced"),
            children: advancedTab
          }
        ]}
      />

      <Button
        danger
        icon={<DeleteOutlined />}
        onClick={() => removeWidget(widget.id)}
      >
        {t("reports.settings.removeWidget")}
      </Button>
    </Flex>
  )
}
